import type { QueryClient, QueryKey } from '@tanstack/react-query';
import { ApiError } from '@/api/client';
import { queryClient } from '@/queryClient';
import { useConnection } from '@/stores/connection';

/**
 * What the screens do with something that was never loaded on this PC, when
 * there is no line to load it now (.docs/023).
 *
 * The query cache holds a query back while offline. With figures loaded
 * before, that is right: they show, unchanged. With none, the screen would say
 * "Loading…" for ever — so it asks this instead, and says what is true.
 */

/** Said wherever something cannot be shown for want of the line. */
export const NOT_ON_THIS_PC = 'Not loaded on this PC before the connection went. It will appear when the connection is back.';

/** Has nothing to show, and is waiting for the line to fetch it. */
export function isOfflineGap(query: { status: string; fetchStatus: string }): boolean {
  return query.status === 'pending' && query.fetchStatus === 'paused';
}

/** Something needed was never loaded here, and there is no line to load it. */
export class NotOnThisPcError extends Error {
  constructor(what: string) {
    super(`${what} was not loaded on this PC before the connection went. Try again when the connection is back.`);
    this.name = 'NotOnThisPcError';
  }
}

/** True for an answer that never came: no line, or no server behind the proxy. */
export function noLine(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 0 || error.status === 502 || error.status === 504);
}

/**
 * Reads `key` from the server, or — with no line — what this PC last loaded
 * under it. Never waits for the line: it answers or throws at once. `what`
 * names it for the message when there is nothing kept either.
 */
export async function fetchOrCached<T>(
  key: QueryKey,
  fetch: () => Promise<T>,
  what: string,
  client: QueryClient = queryClient,
): Promise<T> {
  const kept = () => client.getQueryData<T>(key);
  if (!useConnection.getState().online) {
    const data = kept();
    if (data !== undefined) return data;
    throw new NotOnThisPcError(what);
  }
  try {
    return await client.fetchQuery({ queryKey: key, queryFn: fetch, staleTime: 0, networkMode: 'always', retry: false });
  } catch (error) {
    if (!noLine(error)) throw error;
    const data = kept();
    if (data !== undefined) return data;
    throw new NotOnThisPcError(what);
  }
}
